import mitt, { Emitter, Handler } from 'mitt';

// 事件类型定义
export type Events = {
  // 插件事件
  'plugin:loaded': { name: string };
  'plugin:unloaded': { name: string };
  
  // 路由事件
  'route:changed': { path: string; params?: Record<string, string> };
  
  // AI 侧边栏事件
  'ai:open': void;
  'ai:close': void;
  'ai:toggle': void;
  'ai:query': { prompt: string; context?: unknown };
  'ai:response': { content: string; actions?: AIActionResult[] };
  
  // 账号事件
  'account:created': { id: number; nickname: string };
  'account:updated': { id: number };
  'account:deleted': { id: number };
  'account:statusChanged': { id: number; status: string };
  
  // 内容事件
  'content:created': { id: number };
  'content:published': { id: number; accountId: number };
  'content:scheduled': { id: number; publishAt: string };
  
  // 通用UI事件
  'toast:show': { message: string; type?: 'success' | 'error' | 'info' | 'warning' };
  'modal:open': { component: string; props?: Record<string, unknown> };
  'modal:close': void;
  
  // 通用数据刷新事件
  'data:refresh': { entity: string; id?: number };
};

export interface AIActionResult {
  id: string;
  name: string;
  success: boolean;
  data?: unknown;
}

// 创建全局事件总线
const emitter: Emitter<Events> = mitt<Events>();

// 事件总线 API
export const eventBus = {
  /**
   * 发送事件
   */
  emit: <K extends keyof Events>(event: K, payload: Events[K]) => {
    emitter.emit(event, payload);
  },

  /**
   * 监听事件
   */
  on: <K extends keyof Events>(event: K, handler: Handler<Events[K]>) => {
    emitter.on(event, handler);
    // 返回取消订阅函数
    return () => emitter.off(event, handler);
  },

  /**
   * 取消监听
   */
  off: <K extends keyof Events>(event: K, handler: Handler<Events[K]>) => {
    emitter.off(event, handler);
  },

  /**
   * 监听一次
   */
  once: <K extends keyof Events>(event: K, handler: Handler<Events[K]>) => {
    const wrappedHandler: Handler<Events[K]> = (payload) => {
      handler(payload);
      emitter.off(event, wrappedHandler);
    };
    emitter.on(event, wrappedHandler);
  },

  /**
   * 清除所有监听器
   */
  clear: () => {
    emitter.all.clear();
  },
};

export default eventBus;

